import { useCallback, useEffect, useState } from 'react'
import { ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Dialog } from '../../components/Dialog'
import {
  ApiError,
  exports_,
  type DestinationDraft,
  type ExportDestination,
  type ExportPreview
} from '../../lib/api'

const FORMATS = [
  { value: 'rfc5424', label: 'Syslog (RFC 5424)' },
  { value: 'cef', label: 'CEF (ArcSight)' },
  { value: 'leef', label: 'LEEF 2.0 (QRadar)' },
  { value: 'ocsf-json', label: 'OCSF JSON' }
]

const TRANSPORTS = [
  { value: 'tcp', label: 'TCP' },
  { value: 'udp', label: 'UDP (lossy)' },
  { value: 'http', label: 'HTTP' }
]

const SEVERITIES = [
  { value: 0, label: 'Everything' },
  { value: 2, label: 'Low and above' },
  { value: 3, label: 'Medium and above' },
  { value: 4, label: 'High and above' },
  { value: 5, label: 'Critical only' }
]

const EMPTY: DestinationDraft = {
  name: '',
  format: 'cef',
  transport: 'tcp',
  host: '',
  port: 6514,
  url: '',
  useTls: true,
  minimumSeverity: 0,
  messageFormat: 'text',
  enabled: true
}

/**
 * Where events are copied to, and exactly what arrives when they get there.
 *
 * The preview is the reason this page is worth opening before anything is configured. A SIEM
 * engineer's first question is what the records look like in their console, and the renderers are
 * pure functions of (event, destination) — so the page can answer it exactly, with no destination,
 * no device, and nothing sent.
 *
 * Health lives beside configuration rather than on a separate page: "is this working" is the question
 * somebody comes here to answer, and a pending count next to the row that owns it answers it without
 * a second click.
 */
export function ExportPage() {
  const destinations = useAsync(() => exports_.destinations(), [])
  const [draft, setDraft] = useState<DestinationDraft>(EMPTY)
  const [editing, setEditing] = useState<string | null>(null)
  const [preview, setPreview] = useState<ExportPreview | null>(null)
  const [shown, setShown] = useState('cef')
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)
  /**
   * The destination a removal is being asked about.
   *
   * Held whole rather than by id, so the question can name what goes with it. Removing took the
   * destination and its queued deliveries with one click and no question, next to Edit, in a row
   * whose name is the only thing separating it from the destination somebody meant.
   */
  const [removing, setRemoving] = useState<ExportDestination | null>(null)
  // The dialog's own refusal, separate from the page's banner. See where it is rendered.
  const [formProblem, setFormProblem] = useState<string | null>(null)

  const loadPreview = useCallback(async (destinationId?: string) => {
    try {
      setPreview(await exports_.preview(destinationId ? { destinationId } : undefined))
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The preview could not be rendered.')
    }
  }, [])

  useEffect(() => {
    void loadPreview()
  }, [loadPreview])

  const run = useCallback(
    async (work: () => Promise<string>) => {
      setBusy(true)
      setFailure(null)
      setNotice(null)
      try {
        setNotice(await work())
        destinations.reload()
      } catch (error) {
        setFailure(error instanceof ApiError ? error.message : 'The request failed.')
      } finally {
        setBusy(false)
      }
    },
    [destinations]
  )

  const add = () => {
    setDraft(EMPTY)
    setEditing(null)
    setFormProblem(null)
    setAdding(true)
  }

  /**
   * Puts the dialog away and the preview back.
   *
   * Editing a destination points the preview at that destination, because what it renders is the
   * thing being changed. Closing has to undo that or the page is left showing one destination's
   * rendering under a table of all of them.
   */
  const close = () => {
    setAdding(false)
    setEditing(null)
    setDraft(EMPTY)
    setFormProblem(null)
    void loadPreview()
  }

  /**
   * Deliberately not `run`. A failure here belongs inside the dialog beside the fields it is about,
   * and `run` reports into the page's banner - which the dialog is sitting on top of.
   */
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setFormProblem(null)
    setFailure(null)
    setNotice(null)

    const updating = editing !== null
    try {
      await exports_.save(
        {
          ...draft,
          host: draft.transport === 'http' ? null : draft.host,
          port: draft.transport === 'http' ? null : Number(draft.port),
          url: draft.transport === 'http' ? draft.url : null
        },
        editing ?? undefined
      )
      close()
      setNotice(updating ? 'Destination updated.' : 'Destination saved. New events start going there now.')
      destinations.reload()
    } catch (error) {
      // Kept open, with everything still typed in it. Eight fields is a great deal to ask somebody
      // to enter twice because a port was out of range.
      setFormProblem(error instanceof ApiError ? error.message : 'The destination could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const edit = (destination: ExportDestination) => {
    setAdding(false)
    setFormProblem(null)
    setEditing(destination.destinationId)
    setDraft({
      name: destination.name,
      format: destination.format,
      transport: destination.transport,
      host: destination.host ?? '',
      port: destination.port ?? 6514,
      url: destination.url ?? '',
      useTls: destination.useTls,
      facility: destination.facility,
      minimumSeverity: destination.minimumSeverity,
      messageFormat: destination.messageFormat,
      enabled: destination.enabled
    })
    void loadPreview(destination.destinationId)
  }

  const remove = (destination: ExportDestination) =>
    run(async () => {
      await exports_.remove(destination.destinationId)
      if (editing === destination.destinationId) {
        setEditing(null)
        setDraft(EMPTY)
        setFormProblem(null)
      }
      return `${destination.name} removed. Its queued deliveries went with it.`
    })

  const replay = (destination: ExportDestination) =>
    run(async () => {
      const result = await exports_.replay(destination.destinationId)
      return result.replayed === 0
        ? 'Nothing was parked for that destination.'
        : `${result.replayed} parked deliver${result.replayed === 1 ? 'y is' : 'ies are'} queued again.`
    })

  const items = destinations.value?.items ?? []
  const showing = preview?.formats.find((format) => format.format === shown) ?? preview?.formats[0]

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Export</h1>
          <p>Where events are copied to, and exactly what arrives when they get there.</p>
        </div>
      </div>

      <ErrorBanner message={failure ?? destinations.error} />
      {notice && (
        <div className="banner success" role="status" data-testid="export-notice">
          {notice}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>What a SIEM receives</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            {preview
              ? `A ${preview.className} at severity ${preview.severityId}, rendered by the same code that
                 sends the real thing.`
              : 'Rendering…'}
          </p>

          <div className="toolbar">
            {preview?.formats.map((format) => (
              <button
                key={format.format}
                type="button"
                className={format.format === shown ? 'primary' : undefined}
                onClick={() => setShown(format.format)}
                data-testid={`preview-${format.format}`}
              >
                {format.format}
              </button>
            ))}
          </div>

          {showing && (
            <>
              <pre className="mono" data-testid="preview-payload" style={{ whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                {showing.payload}
              </pre>
              <p className="muted" data-testid="preview-framing">
                {showing.bytes} bytes on the wire · {showing.framing}
              </p>
            </>
          )}
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="card-heading">
            <h3 style={{ marginTop: 0 }}>Destinations</h3>
            <button type="button" className="primary" onClick={add} disabled={busy} data-testid="add-destination">
              Add destination
            </button>
          </div>

          {items.length === 0 ? (
            <div className="banner" data-testid="no-destinations">
              Nothing is forwarded yet. A destination added here receives every event at or above its
              severity, and keeps what it could not deliver until it can.
            </div>
          ) : (
            <div className="table-scroll">
              <table data-testid="destination-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Format</th>
                    <th>Target</th>
                    <th>Health</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((destination) => (
                    <tr key={destination.destinationId} data-testid={`destination-${destination.name}`}>
                      <td>
                        {destination.name}
                        {!destination.enabled && <span className="pill"> paused</span>}
                      </td>
                      <td>
                        <span className="pill">{destination.format}</span>
                      </td>
                      <td className="mono secondary">
                        {destination.transport === 'http'
                          ? destination.url
                          : `${destination.transport}://${destination.host}:${destination.port}`}
                      </td>
                      <td>
                        {destination.consecutiveFailures > 0 ? (
                          <span data-testid={`failing-${destination.name}`}>
                            {destination.consecutiveFailures} failed · {destination.lastFailure}
                          </span>
                        ) : (
                          <span className="muted">
                            {destination.lastSuccessUtc
                              ? `last delivered ${formatUtc(destination.lastSuccessUtc)}`
                              : 'nothing sent yet'}
                          </span>
                        )}
                        {(destination.pendingDeliveries > 0 || destination.parkedDeliveries > 0) && (
                          <div className="muted">
                            {destination.pendingDeliveries} waiting · {destination.parkedDeliveries} parked
                          </div>
                        )}
                      </td>
                      <td>
                        <button type="button" onClick={() => edit(destination)} disabled={busy}>
                          Edit
                        </button>
                        {destination.parkedDeliveries > 0 && (
                          <button
                            type="button"
                            onClick={() => replay(destination)}
                            disabled={busy}
                            data-testid={`replay-${destination.name}`}
                          >
                            Replay parked
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setRemoving(destination)}
                          disabled={busy}
                          data-testid={`remove-${destination.name}`}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={adding || editing !== null}
        title={editing ? 'Edit destination' : 'New destination'}
        onClose={close}
        wide
        data-testid="destination-dialog"
      >
        {/*
          A form rather than a bare group of fields, which it was not before. Enter submits, the
          browser enforces what it can, and a dialog behaves the way a dialog is expected to.
        */}
        <form onSubmit={save} data-testid="destination-form">
          <div className="toolbar">
            <label className="field">
              Name
              <input
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                data-testid="destination-name"
              />
            </label>
            <label className="field">
              Format
              <select
                value={draft.format}
                onChange={(event) => setDraft({ ...draft, format: event.target.value })}
                data-testid="destination-format"
              >
                {FORMATS.map((format) => (
                  <option key={format.value} value={format.value}>
                    {format.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Transport
              <select
                value={draft.transport}
                onChange={(event) => setDraft({ ...draft, transport: event.target.value })}
                data-testid="destination-transport"
              >
                {TRANSPORTS.map((transport) => (
                  <option key={transport.value} value={transport.value}>
                    {transport.label}
                  </option>
                ))}
              </select>
            </label>

            {draft.transport === 'http' ? (
              <label className="field">
                URL
                <input
                  value={draft.url ?? ''}
                  onChange={(event) => setDraft({ ...draft, url: event.target.value })}
                  data-testid="destination-url"
                />
              </label>
            ) : (
              <>
                <label className="field">
                  Host
                  <input
                    value={draft.host ?? ''}
                    onChange={(event) => setDraft({ ...draft, host: event.target.value })}
                    data-testid="destination-host"
                  />
                </label>
                <label className="field">
                  Port
                  <input
                    type="number"
                    value={draft.port ?? 6514}
                    onChange={(event) => setDraft({ ...draft, port: Number(event.target.value) })}
                    data-testid="destination-port"
                  />
                </label>
                <label className="field">
                  TLS
                  {/* On by default. A SIEM feed is the organization's security telemetry in
                      plaintext on the wire, so turning this off is a decision somebody makes
                      deliberately rather than a default they inherit. */}
                  <input
                    type="checkbox"
                    checked={draft.useTls}
                    onChange={(event) => setDraft({ ...draft, useTls: event.target.checked })}
                    data-testid="destination-tls"
                  />
                </label>
              </>
            )}

            <label className="field">
              Send
              <select
                value={draft.minimumSeverity ?? 0}
                onChange={(event) => setDraft({ ...draft, minimumSeverity: Number(event.target.value) })}
                data-testid="destination-severity"
              >
                {SEVERITIES.map((severity) => (
                  <option key={severity.value} value={severity.value}>
                    {severity.label}
                  </option>
                ))}
              </select>
            </label>

          </div>

          {draft.transport === 'udp' && (
            <p className="muted" data-testid="udp-warning">
              UDP has no acknowledgment. A successful send means the datagram left this server and
              nothing more, and anything over 1024 bytes is truncated. Use TCP for a destination that
              matters.
            </p>
          )}

          {/*
            Inside the dialog, not in the page's banner behind it. Every rule about a destination is
            the server's - an absolute URL for HTTP, a port in range, OCSF JSON only over syslog
            framing - so the first anybody hears of a mistake is a round trip, and reporting it on
            the page underneath would hide it behind the thing that caused it.
          */}
          {formProblem && (
            <p className="error" role="alert" data-testid="destination-problem">
              {formProblem}
            </p>
          )}

          <div className="dialog-actions">
            <button type="button" onClick={close} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={busy} data-testid="save-destination">
              {editing ? 'Save changes' : 'Add destination'}
            </button>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        title={removing ? `Remove ${removing.name}` : 'Remove destination'}
        confirmLabel="Remove it"
        danger
        busy={busy}
        onConfirm={() => {
          if (!removing) return
          const destination = removing
          setRemoving(null)
          remove(destination)
        }}
        onClose={() => setRemoving(null)}
        data-testid="remove-destination-confirm"
      >
        {removing && removing.pendingDeliveries + removing.parkedDeliveries > 0 ? (
          <div className="banner warning" role="note" data-testid="remove-destination-queued">
            {removing.pendingDeliveries + removing.parkedDeliveries} deliver
            {removing.pendingDeliveries + removing.parkedDeliveries === 1 ? 'y is' : 'ies are'} queued
            for this destination and go with it. Those events are still here and still exported to
            every other destination; they are simply never sent to this one.
          </div>
        ) : null}

        <p>
          Nothing stops being collected and nothing is deleted from this server — the SIEM at the
          other end just stops receiving. Adding it back is a new destination, which starts from now
          rather than replaying what it missed.
        </p>
      </ConfirmDialog>
    </>
  )
}
